import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { useStyles } from "./CorporationDetails.styles";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Button, IconButton } from "@mui/material";
import { MainTable } from "components";
import { useCallback, useMemo, useState } from "react";
import { MillForm } from "../../MillForm/MillForm.view";
import { useTranslation } from "react-i18next";
import { ICorporateDetailRow } from "./CorporationDetailsInterfaces";
import { useCorporationsDetailsColumns } from "./hooks/useCorporationsDetailsColumns";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { MADERA_BACKOFFICE_PATHS } from "constants/constants";
import { IMill } from "../../CorporationsAPIInterfaces";
import { FeatureToggleConfirmModal } from "pages/Osorno/components";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useFeatureToggleModal } from "pages/Osorno/hooks/useFeatureToggleModal";

const CorporationDetails = () => {
    const { corporationId } = useParams();
    const { corporate, millsWithNoDivision, loading, error, refetch } = CorporationsEntity.GetCorporationDetail(
        Number(corporationId),
    );
    const [millData, setMillData] = useState<IMill | null>(null);
    const aspenModal = useFeatureToggleModal<ICorporateDetailRow>(
        "setup_corporation.corporation_mills.aspen_modal",
    );
    const clusterModal = useFeatureToggleModal<ICorporateDetailRow>(
        "setup_corporation.corporation_mills.cluster_modal",
    );
    const millEnabledModal = useFeatureToggleModal<ICorporateDetailRow>(
        "setup_corporation.corporation_mills.mill_enabled_modal",
    );
    const dedicatedLocalserverModal = useFeatureToggleModal<ICorporateDetailRow>(
        "setup_corporation.corporation_mills.dedicated_localserver_modal",
    );
    const classes = useStyles();
    const { t } = useTranslation();
    const { activateAspenPlus, deactivateAspenPlus } = MillsEntity.UpsertMillModuleAspenPlus();
    const { activateClusterFeature, deactivateClusterFeature } = MillsEntity.UpdateMillClusterFeature();
    const { updateEnableOrDisableMill } = MillsEntity.EnableOrDisableMill();
    const { updateDedicatedLocalserver } = MillsEntity.UpdateMillDedicatedLocalserver();

    const handleAspenPlusToggleClick = useCallback(
        (row: ICorporateDetailRow, enable: boolean) => aspenModal.openModal(row, enable),
        [aspenModal],
    );

    const handleClusterToggleClick = useCallback(
        (row: ICorporateDetailRow, enable: boolean) => clusterModal.openModal(row, enable),
        [clusterModal],
    );

    const handleMillEnabledToggleClick = useCallback(
        (row: ICorporateDetailRow, enable: boolean) => millEnabledModal.openModal(row, enable),
        [millEnabledModal],
    );

    const handleDedicatedLocalserverToggleClick = useCallback(
        (row: ICorporateDetailRow, enable: boolean) => dedicatedLocalserverModal.openModal(row, enable),
        [dedicatedLocalserverModal],
    );

    const isCorporateAspenEnabled = useMemo(() => {
        const licenses = corporate?.corporate_module_licenses;
        return Boolean(licenses?.length && licenses[0]?.is_enabled);
    }, [corporate?.corporate_module_licenses]);

    const tableColumns = useCorporationsDetailsColumns(
        handleAspenPlusToggleClick,
        handleClusterToggleClick,
        handleMillEnabledToggleClick,
        handleDedicatedLocalserverToggleClick,
        isCorporateAspenEnabled,
    ) as unknown as IMainTableColumn[];
    const navigate = useNavigate();

    const corporateDetailData = useMemo(() => {
        const corporateDetailRows = [] as ICorporateDetailRow[];
        if (corporate) {
            const mills = [];
            if (corporate.divisions) {
                corporate.divisions.forEach((division) => {
                    if (division.regions) {
                        division.regions.forEach((region) => {
                            if (region.mills) {
                                region.mills.forEach((mill) => {
                                    mills.push(mill);
                                    corporateDetailRows.push({
                                        id: mill.mill_id,
                                        name: mill.name,
                                        location: mill.physical_address,
                                        division: division.name,
                                        region: region.name,
                                        admin:
                                            mill.users && mill.users?.length > 0
                                                ? `${mill.users?.[0]?.first_name} ${mill.users?.[0]?.last_name}`
                                                : "",
                                        date: mill.created_at,
                                        module: corporate.corporate_module_licenses?.[0]?.module_license?.name,
                                        enabled: mill.is_enabled,
                                        clusterEnabled: mill.cluster_enabled,
                                        dedicatedLocalserver: mill.dedicated_localserver ?? false,
                                        aspenPlusEnabled: mill.mill_modules?.[0]?.is_enabled ?? false,
                                    });
                                });
                            }
                        });
                    }
                });
            }
            if (millsWithNoDivision.length > 0) {
                millsWithNoDivision.forEach((mill) => {
                    mills.push(mill);
                    corporateDetailRows.push({
                        id: mill.mill_id,
                        name: mill.name,
                        location: mill.physical_address,
                        division: undefined,
                        region: undefined,
                        admin:
                            mill.users && mill.users?.length > 0
                                ? `${mill.users?.[0]?.first_name} ${mill.users?.[0]?.last_name}`
                                : "",
                        date: mill.created_at,
                        module: corporate.corporate_module_licenses?.[0]?.module_license?.name,
                        enabled: mill.is_enabled,
                        clusterEnabled: mill.cluster_enabled,
                        dedicatedLocalserver: mill.dedicated_localserver ?? false,
                        aspenPlusEnabled: mill.mill_modules?.[0]?.is_enabled ?? false,
                    });
                });
            }
        }

        return corporateDetailRows.sort((a, b) => a.id! - b.id!);
    }, [corporate, millsWithNoDivision]);

    const handleAspenModalConfirm = async () => {
        if (aspenModal.row?.id) {
            if (aspenModal.enable) {
                await activateAspenPlus(aspenModal.row.id);
            } else {
                await deactivateAspenPlus(aspenModal.row.id);
            }
            await refetch();
        }
    };

    const handleClusterModalConfirm = async () => {
        if (clusterModal.row?.id) {
            if (clusterModal.enable) {
                await activateClusterFeature(clusterModal.row.id);
            } else {
                await deactivateClusterFeature(clusterModal.row.id);
            }
            await refetch();
        }
    };

    const handleMillEnabledModalConfirm = async () => {
        if (millEnabledModal.row?.id) {
            await updateEnableOrDisableMill({
                variables: {
                    mill_id: millEnabledModal.row.id,
                    is_enabled: millEnabledModal.enable,
                },
            });
            await refetch();
        }
    };

    const handleDedicatedLocalserverModalConfirm = async () => {
        if (dedicatedLocalserverModal.row?.id) {
            await updateDedicatedLocalserver(
                dedicatedLocalserverModal.row.id,
                dedicatedLocalserverModal.enable,
            );
            await refetch();
        }
    };

    const handleOnRowClick = (row: Object) => {
        const millId = (row as ICorporateDetailRow).id;
        corporate?.divisions?.forEach((division) => {
            division.regions?.forEach((region) => {
                region.mills?.forEach((mill) => {
                    if (mill.mill_id === millId) {
                        setMillData(mill);
                    }
                });
            });
        });
        millsWithNoDivision.forEach((mill) => {
            if (mill.mill_id === millId) {
                setMillData(mill);
            }
        });
    };

    return (
        <div className={classes.wrapper}>
            {!millData && (
                <>
                    <FeatureToggleConfirmModal
                        {...aspenModal.modalProps}
                        onConfirm={handleAspenModalConfirm}
                    />
                    <FeatureToggleConfirmModal
                        {...clusterModal.modalProps}
                        onConfirm={handleClusterModalConfirm}
                    />
                    <FeatureToggleConfirmModal
                        {...millEnabledModal.modalProps}
                        onConfirm={handleMillEnabledModalConfirm}
                    />
                    <FeatureToggleConfirmModal
                        {...dedicatedLocalserverModal.modalProps}
                        onConfirm={handleDedicatedLocalserverModalConfirm}
                    />
                    <div className={classes.title}>
                        <IconButton
                            onClick={() =>
                                navigate(
                                    `/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.ROOT}`,
                                )
                            }
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                        {corporate?.name}
                    </div>
                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            className={classes.addButton}
                            onClick={() => setMillData({} as IMill)}
                        >
                            <AddIcon fontSize="small" style={{ marginRight: "5px" }} />
                            {t("setup_corporation.corporation_mills.add_mill_button")}
                        </Button>
                    </div>
                    <div>
                        <MainTable
                            columns={tableColumns}
                            rows={!loading && !error ? corporateDetailData : []}
                            loading={loading}
                            onRowClick={(row: Object) => {
                                handleOnRowClick(row);
                            }}
                            pagination
                        />
                    </div>
                </>
            )}
            {!!millData && (
                <MillForm
                    millData={millData}
                    onCancel={() => setMillData(null)}
                    corporation={corporate && corporate}
                    hideSameAsCorporationAddress
                    closeForm={() => {
                        refetch();
                        setMillData(null);
                    }}
                />
            )}
        </div>
    );
};

export default CorporationDetails;
